import React from "react";
import { Label } from "@/components/form";

interface TextAreaFieldProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  label?: string; // Made optional
  placeholder?: string;
  rows?: number;
  required?: boolean;
  error?: boolean | string;
  hint?: string;
  helpText?: string; // Alias for hint (back-compat)
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  maxLength?: number;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
}

const TextAreaField: React.FC<TextAreaFieldProps> = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  onKeyDown,
  label, // Now optional
  placeholder = "Enter your text here...",
  rows = 4,
  required = false,
  error = false,
  hint,
  helpText,
  className = "",
  inputClassName = "",
  disabled = false,
  maxLength,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}) => {
  // Build textarea classes using TailAdmin styling
  const getTextAreaClasses = () => {
    let baseClasses = "w-full rounded-lg border appearance-none px-4 py-2.5 text-base shadow-sm placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 resize-vertical";
    
    if (disabled) {
      return `${baseClasses} text-gray-500 border-gray-300 opacity-40 bg-gray-100 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700`;
    } else if (error) {
      return `${baseClasses} border-error-500 focus:border-error-300 focus:ring-error-500/20 dark:text-error-400 dark:border-error-500 dark:focus:border-error-800`;
    } else {
      return `${baseClasses} bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800`;
    }
  };

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      )}
      <textarea
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={rows}
        required={required}
        disabled={disabled}
        maxLength={maxLength}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        className={`${getTextAreaClasses()} ${inputClassName}`.trim()}
      />
      {((hint ?? helpText) || (typeof error === 'string' && error)) && (
        <p
          className={`mt-1.5 text-xs ${
            error
              ? "text-error-500"
              : "text-gray-500"
          }`}
        >
          {typeof error === 'string' ? error : (hint ?? helpText)}
        </p>
      )}
    </div>
  );
};

// Named export for consistency with other form fields
export { TextAreaField };

// Default export for backward compatibility
export default TextAreaField; 